import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { amenitiesService } from "@/services/amenitiesService";
import {
  AmenitiesFilters,
  AmenitiesFindAllResponse,
  CreateAmenityDto,
  ReorderAmenitiesDto,
  UpdateAmenityDto,
} from "@/services/interfaces/IAmenitiesService";
import { orderAmenitiesByIds } from "@/components/pages/amenities/utils/amenityHomeRow";

export const amenityKeys = {
  all: ["amenities"] as const,
  lists: () => [...amenityKeys.all, "list"] as const,
  list: (filters?: AmenitiesFilters) =>
    [...amenityKeys.lists(), filters] as const,
  categories: (hotelId?: string) =>
    [...amenityKeys.all, "categories", hotelId] as const,
  home: (hotelId?: string) => [...amenityKeys.all, "home", hotelId] as const,
  details: () => [...amenityKeys.all, "detail"] as const,
  detail: (id: string) => [...amenityKeys.details(), id] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateAmenityRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: amenityKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useAmenities = (filters?: AmenitiesFilters) => {
  return useQuery({
    queryKey: amenityKeys.list(filters),
    queryFn: () => amenitiesService.getAll(filters),
    enabled: !!filters?.hotelId,
  });
};

/** Starred amenities of the hotel in guest home order. */
export const useHomeAmenities = (hotelId?: string) => {
  return useQuery({
    queryKey: amenityKeys.home(hotelId),
    queryFn: () =>
      amenitiesService.getAll({
        hotelId,
        showOnHome: true,
        sortBy: "homeOrder",
        sortOrder: "asc",
      }),
    enabled: !!hotelId,
  });
};

export const useAmenity = (id: string) => {
  return useQuery({
    queryKey: amenityKeys.detail(id),
    queryFn: () => amenitiesService.getById(id),
    enabled: !!id,
  });
};

export const useAmenityCategories = (hotelId?: string) => {
  return useQuery({
    queryKey: amenityKeys.categories(hotelId),
    queryFn: () => amenitiesService.getCategories(hotelId!),
    enabled: !!hotelId,
  });
};

export const useCreateAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAmenityDto) => amenitiesService.create(data),
    onSuccess: () => {
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};

export const useUpdateAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAmenityDto }) =>
      amenitiesService.update(id, data),
    onSuccess: (updatedAmenity) => {
      queryClient.setQueryData(
        amenityKeys.detail(updatedAmenity.id),
        updatedAmenity,
      );
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};

/**
 * Shows the dragged order at once; on an error the previous order comes back (the toast comes
 * from BaseService), then the list is read from the server either way.
 */
export const useReorderAmenities = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderAmenitiesDto) => amenitiesService.reorder(data),
    onMutate: async ({ hotelId, ids }) => {
      const key = amenityKeys.home(hotelId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AmenitiesFindAllResponse>(key);
      if (previous) {
        queryClient.setQueryData<AmenitiesFindAllResponse>(key, {
          ...previous,
          items: orderAmenitiesByIds(previous.items, ids),
        });
      }
      return { previous };
    },
    onError: (_error, { hotelId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(amenityKeys.home(hotelId), context.previous);
      }
    },
    onSettled: () => {
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};

export const useDeleteAmenity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => amenitiesService.deleteAmenity(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: amenityKeys.detail(deletedId) });
      invalidateAmenityRelatedQueries(queryClient);
    },
  });
};
